/* eslint-disable i18next/no-literal-string */
import {EntityType} from '@/types/entity-link'

type Id = string | number

/**
 * Hierarchical route definitions.
 *
 * Each branch node exposes a `segment` property (for React Router <Route path={segment}>).
 * Full paths are composed from parent paths — each segment string is defined exactly once.
 */
export const routes = (() => {
    // Top-level paths
    const profile = '/profile'
    const my = '/my'
    const admin = '/admin'
    const schema = '/schema'
    const idMgmt = '/identity-management'
    const systems = '/systems'
    const accGov = '/access-governance'
    const bgJobs = '/background-jobs'
    const effAcc = '/effective-access'
    const prov = '/access-provisioning'
    const auditLog = '/audit-log'
    const reporting = '/reporting'
    const connectors = '/connectors'

    // Second-level paths
    const myRequests = `${my}/requests`
    const myFunctionGroupRequests = `${my}/function-group-requests`
    const myRehires = `${my}/rehires`
    const myReviews = `${my}/reviews`
    const adminRoles = `${admin}/roles`
    const inboundConnectorsRoot = `${connectors}/inbound`
    const outboundConnectorsRoot = `${connectors}/outbound`
    const schemaTypes = `${schema}/types`
    const schemaAttrDefs = `${schema}/attribute-definitions`
    const schemaRelSchema = `${schema}/relationship-schema`
    const schemaFormLayouts = `${schema}/form-layouts`
    const schemaValueLists = `${schema}/value-lists`
    const schemaPolicies = `${schema}/policies`
    const schemaViolations = `${schema}/violations`
    const adminErrorLogs = `${admin}/error-logs`
    const adminScriptExecuteLog = `${admin}/script-execute-log`
    const adminRequestInspector = `${admin}/request-inspector`
    const adminCaches = `${admin}/caches`
    const adminLlmPrompts = `${admin}/llm-prompts`
    const adminNotificationTemplates = `${admin}/notification-templates`
    const adminTestData = `${admin}/test-data`
    const adminRehireReview = `${admin}/rehire-review`
    const adminLifecycleEvents = `${admin}/lifecycle-events`
    const adminServiceAccounts = `${admin}/service-accounts`
    const idParties = `${idMgmt}/parties`
    const idIdentities = `${idMgmt}/identities`
    const sysList = `${systems}/list`
    const sysResources = `${systems}/resources`
    const sysEnforcement = `${systems}/enforcement-objects`
    const govProfiles = `${accGov}/profiles`
    const govRules = `${accGov}/rules`
    const govRuleReqs = `${accGov}/auto-assign-rule-requests`
    const govAccessReqs = `${accGov}/access-requests`
    const govPolicies = `${accGov}/approval-policies`
    const govEscalationThresholds = `${accGov}/escalation-thresholds`
    const govCertPolicies = `${accGov}/certification-policies`
    const govCertCampaigns = `${accGov}/certification-campaigns`
    const govFunctionGroups = `${accGov}/function-groups`
    const govFunctionGroupRequests = `${accGov}/function-group-requests`
    const govRestrictionRules = `${accGov}/restriction-rules`
    const govRestrictionRuleReqs = `${accGov}/restriction-rule-requests`
    const govCertReviews = `${accGov}/certification-reviews`
    const govCertDashboard = `${accGov}/certification-dashboard`
    const govViolationsDashboard = `${accGov}/violation-dashboard`
    const jobsList = `${bgJobs}/jobs`
    const jobsExec = `${bgJobs}/executions`
    const jobBuilder = `${bgJobs}/builder`
    const effIdentities = `${effAcc}/identities`
    const effSystems = `${effAcc}/systems`

    return {
        dashboard: () => '/',
        profile: () => profile,

        my: {
            segment: 'my',
            root: () => my,
            access: {
                segment: 'access',
                path: () => `${my}/access`,
                withAssignment: (id: Id) => `${my}/access?assignmentId=${id}`,
            },
            responsibilities: {
                segment: 'responsibilities',
                path: () => `${my}/responsibilities`,
                tab: (key: string) => `${my}/responsibilities?tab=${key}`,
            },
            requests: {
                segment: 'requests',
                list: () => myRequests,
                create: () => `${myRequests}/new`,
                detail: (id: Id) => `${myRequests}/${id}`,
            },
            approvals: {
                segment: 'approvals',
                path: () => `${my}/approvals`,
            },
            functionGroupRequests: {
                segment: 'function-group-requests',
                list: () => `${my}/requests`,
                create: () => `${myFunctionGroupRequests}/new`,
                detail: (id: Id) => `${myFunctionGroupRequests}/${id}`,
            },
            notifications: {
                segment: 'notifications',
                path: () => `${my}/notifications`,
                detail: (id: Id) => `${my}/notifications/${id}`,
            },
            delegations: {
                segment: 'delegations',
                path: () => `${my}/delegations`,
            },
            rehires: {
                segment: 'rehires',
                list: () => myRehires,
                detail: (id: Id) => `${myRehires}/${id}`,
            },
            reviews: {
                segment: 'reviews',
                list: () => myReviews,
                assignments: (itemId: Id) => `${myReviews}/${itemId}/assignments`,
            },
        },

        admin: {
            segment: 'admin',
            root: () => admin,
            roles: {
                segment: 'roles',
                list: () => adminRoles,
                detail: (id: Id) => `${adminRoles}/${id}`,
            },
            roleAssignments: {
                segment: 'role-assignments',
                path: () => `${admin}/role-assignments`,
            },
            notificationLog: {
                segment: 'notification-log',
                path: () => `${admin}/notification-log`,
            },
            errorLogs: {
                segment: 'error-logs',
                list: () => adminErrorLogs,
                detail: (id: Id) => `${adminErrorLogs}/${id}`,
                byCorrelation: (correlationId: Id) =>
                    `${adminErrorLogs}?correlationId=${correlationId}`,
            },
            scriptExecuteLog: {
                segment: 'script-execute-log',
                list: () => adminScriptExecuteLog,
            },
            requestInspector: {
                segment: 'request-inspector',
                list: () => adminRequestInspector,
                detail: (sessionId: Id) => `${adminRequestInspector}/${sessionId}`,
                requestDetail: (sessionId: Id, spanId: Id) => `${adminRequestInspector}/${sessionId}/request/${spanId}`,
            },
            caches: {
                segment: 'caches',
                list: () => adminCaches,
            },
            llmPrompts: {
                segment: 'llm-prompts',
                list: () => adminLlmPrompts,
                detail: (key: string) => `${adminLlmPrompts}/${encodeURIComponent(key)}`,
            },
            notificationTemplates: {
                segment: 'notification-templates',
                list: () => adminNotificationTemplates,
                detail: (key: Id) => `${adminNotificationTemplates}/${key}`,
            },
            testData: {
                segment: 'test-data',
                path: () => adminTestData,
            },
            rehireReview: {
                segment: 'rehire-review',
                list: () => adminRehireReview,
                detail: (id: Id) => `${adminRehireReview}/${id}`,
            },
            lifecycleEvents: {
                segment: 'lifecycle-events',
                path: () => adminLifecycleEvents,
            },
            serviceAccounts: {
                segment: 'service-accounts',
                list: () => adminServiceAccounts,
                detail: (id: Id) => `${adminServiceAccounts}/${id}`,
            },
        },

        connectors: {
            segment: 'connectors',
            root: () => connectors,
            inbound: {
                segment: 'inbound',
                list: () => inboundConnectorsRoot,
                detail: (id: Id) => `${inboundConnectorsRoot}/${id}`,
                configuration: {
                    segment: 'configuration',
                    root: (id: Id) => `${inboundConnectorsRoot}/${id}/configuration`,
                    connection: (id: Id) => `${inboundConnectorsRoot}/${id}/configuration/connection`,
                    reconciliation: (id: Id) => `${inboundConnectorsRoot}/${id}/configuration/reconciliation`,
                    operations: (id: Id) => `${inboundConnectorsRoot}/${id}/configuration/operations`,
                },
            },
            outbound: {
                segment: 'outbound',
                list: () => outboundConnectorsRoot,
                createForSystem: (systemId: Id) => `${outboundConnectorsRoot}?createForSystem=${systemId}`,
                detail: (id: Id) => `${outboundConnectorsRoot}/${id}`,
            },
        },

        schema: {
            segment: 'schema',
            root: () => schema,
            types: {
                segment: 'types',
                list: () => schemaTypes,
                detail: (tableName: string) => `${schemaTypes}/${tableName}`,
                valueDetail: (tableName: string, code: string) => `${schemaTypes}/${tableName}/${code}`,
            },
            attributeDefinitions: {
                segment: 'attribute-definitions',
                path: () => schemaAttrDefs,
            },
            formLayouts: {
                segment: 'form-layouts',
                list: () => schemaFormLayouts,
                detail: (useCaseKey: Id) => `${schemaFormLayouts}/${useCaseKey}`,
            },
            relationshipSchema: {
                segment: 'relationship-schema',
                path: () => schemaRelSchema,
                tab: (key: 'entries' | 'violations') => `${schemaRelSchema}?tab=${key}`,
                seedFromViolation: (params: {fromPartyType: string; relationshipType: string; toPartyType: string}) =>
                    `${schemaRelSchema}?dialog=create&fromPartyType=${encodeURIComponent(params.fromPartyType)}&relationshipType=${encodeURIComponent(params.relationshipType)}&toPartyType=${encodeURIComponent(params.toPartyType)}`,
            },
            valueLists: {
                segment: 'value-lists',
                list: () => schemaValueLists,
                detail: (id: Id) => `${schemaValueLists}/${id}`,
            },
            policies: {
                segment: 'policies',
                list: () => schemaPolicies,
                detail: (id: Id) => `${schemaPolicies}/${id}`,
                create: () => `${schemaPolicies}/new`,
                edit: (id: Id) => `${schemaPolicies}/${id}/edit`,
            },
            violations: {
                segment: 'violations',
                list: () => schemaViolations,
                detail: (id: Id) => `${schemaViolations}/${id}`,
            },
        },

        identityManagement: {
            segment: 'identity-management',
            root: () => idMgmt,
            parties: {
                segment: 'parties',
                list: () => idParties,
                detail: (id: Id) => `${idParties}/${id}`,
                byType: (typeCode: string) => `${idParties}/by-type/${encodeURIComponent(typeCode)}`,
                createByType: (typeCode: string) => `${idParties}/by-type/${encodeURIComponent(typeCode)}/new`,
            },
            identities: {
                segment: 'identities',
                list: () => idIdentities,
                detail: (id: Id) => `${idIdentities}/${id}`,
                byType: (typeCode: string) => `${idIdentities}/by-type/${encodeURIComponent(typeCode)}`,
            },
            accounts: {
                segment: 'accounts',
                path: () => `${idMgmt}/accounts`,
                list: () => `${idMgmt}/accounts`,
                detail: (id: Id) => `${idMgmt}/accounts/${id}`,
            },
            onboarding: {
                segment: 'onboard',
                create: () => `${idMgmt}/onboard`,
            },
        },

        systems: {
            segment: 'systems',
            root: () => systems,
            listSegment: 'list',
            list: () => sysList,
            detail: (id: Id) => `${sysList}/${id}`,
            byType: (typeCode: string) => `${sysList}/by-type/${encodeURIComponent(typeCode)}`,
            createByType: (typeCode: string) => `${sysList}/by-type/${encodeURIComponent(typeCode)}/new`,
            resources: {
                segment: 'resources',
                list: () => sysResources,
                detail: (id: Id) => `${sysResources}/${id}`,
                byType: (typeCode: string) => `${sysResources}/by-type/${encodeURIComponent(typeCode)}`,
                createByType: (typeCode: string) => `${sysResources}/by-type/${encodeURIComponent(typeCode)}/new`,
            },
            enforcementObjects: {
                segment: 'enforcement-objects',
                list: () => sysEnforcement,
                detail: (id: Id) => `${sysEnforcement}/${id}`,
            },
        },

        accessGovernance: {
            segment: 'access-governance',
            root: () => accGov,
            profiles: {
                segment: 'profiles',
                list: () => govProfiles,
                create: () => `${govProfiles}/new`,
                detail: (id: Id) => `${govProfiles}/${id}`,
                edit: (id: Id) => `${govProfiles}/${id}/edit`,
            },
            rules: {
                segment: 'rules',
                list: () => govRules,
                detail: (id: Id) => `${govRules}/${id}`,
                executions: () => `${govRules}/executions`,
                executionsForRule: (ruleId: Id) => `${govRules}/executions?ruleId=${ruleId}`,
            },
            autoAssignRuleRequests: {
                segment: 'auto-assign-rule-requests',
                list: () => govRuleReqs,
                create: (queryParams?: string) =>
                    queryParams ? `${govRuleReqs}/new?${queryParams}` : `${govRuleReqs}/new`,
                detail: (id: Id) => `${govRuleReqs}/${id}`,
                edit: (id: Id) => `${govRuleReqs}/${id}/edit`,
            },
            accessRequests: {
                segment: 'access-requests',
                list: () => govAccessReqs,
                create: () => `${govAccessReqs}/new`,
                detail: (id: Id) => `${govAccessReqs}/${id}`,
            },
            functionGroups: {
                segment: 'function-groups',
                list: () => govFunctionGroups,
                create: () => `${govFunctionGroups}/new`,
                detail: (id: Id) => `${govFunctionGroups}/${id}`,
                byType: (typeCode: string) => `${govFunctionGroups}/by-type/${encodeURIComponent(typeCode)}`,
            },
            functionGroupRequests: {
                segment: 'function-group-requests',
                list: () => govFunctionGroupRequests,
                create: () => `${govFunctionGroupRequests}/new`,
                detail: (id: Id) => `${govFunctionGroupRequests}/${id}`,
            },
            approvalPolicies: {
                segment: 'approval-policies',
                list: () => govPolicies,
                create: () => `${govPolicies}/new`,
                detail: (id: Id) => `${govPolicies}/${id}`,
                edit: (id: Id) => `${govPolicies}/${id}/edit`,
            },
            escalationThresholds: {
                segment: 'escalation-thresholds',
                list: () => govEscalationThresholds,
            },
            restrictionRules: {
                segment: 'restriction-rules',
                list: () => govRestrictionRules,
                create: () => `${govRestrictionRules}/new`,
                detail: (id: Id) => `${govRestrictionRules}/${id}`,
                edit: (id: Id) => `${govRestrictionRules}/${id}/edit`,
                detections: () => `${govRestrictionRules}/detections`,
                detectionsForRule: (ruleId: Id) => `${govRestrictionRules}/detections?ruleId=${ruleId}`,
                violationsForRule: (ruleId: Id) => `${govRestrictionRules}/${ruleId}/violations`,
            },
            restrictionRuleRequests: {
                segment: 'restriction-rule-requests',
                list: () => govRestrictionRuleReqs,
                openOnly: () => `${govRestrictionRuleReqs}?status=DRAFT,PENDING`,
                create: () => `${govRestrictionRuleReqs}/new`,
                detail: (id: Id) => `${govRestrictionRuleReqs}/${id}`,
                edit: (id: Id) => `${govRestrictionRuleReqs}/${id}/edit`,
            },
            violationsDashboard: {
                segment: 'violation-dashboard',
                path: () => govViolationsDashboard,
            },
            delegations: {
                segment: 'delegations',
                list: () => `${accGov}/delegations`,
            },
            certificationPolicies: {
                segment: 'certification-policies',
                list: () => govCertPolicies,
                detail: (id: Id) => `${govCertPolicies}/${id}`,
                create: () => `${govCertPolicies}/new`,
                edit: (id: Id) => `${govCertPolicies}/${id}/edit`,
                editAtStep: (id: Id, step: number) => `${govCertPolicies}/${id}/edit?step=${step}`,
            },
            certificationCampaigns: {
                segment: 'certification-campaigns',
                list: () => govCertCampaigns,
                detail: (id: Id) => `${govCertCampaigns}/${id}`,
            },
            certificationReviews: {
                segment: 'certification-reviews',
                list: () => govCertReviews,
                assignments: (itemId: Id) => `${govCertReviews}/${itemId}/assignments`,
            },
            certificationDashboard: {
                segment: 'certification-dashboard',
                path: () => govCertDashboard,
            },
        },

        backgroundJobs: {
            segment: 'background-jobs',
            root: () => bgJobs,
            dashboard: {
                segment: 'dashboard',
                path: () => `${bgJobs}/dashboard`,
            },
            jobs: {
                segment: 'jobs',
                list: () => jobsList,
                detail: (id: Id) => `${jobsList}/${id}`,
            },
            executions: {
                segment: 'executions',
                list: (queryParams?: string) =>
                    queryParams ? `${jobsExec}?${queryParams}` : jobsExec,
                detail: (id: Id) => `${jobsExec}/${id}`,
            },
            builder: {
                segment: 'builder',
                newJob: () => `${jobBuilder}/new`,
                edit: (id: Id) => `${jobBuilder}/${id}`,
                script: (jobId: Id, taskId: Id) => `${jobBuilder}/${jobId}/script/${taskId}`,
            },
            cluster: {
                segment: 'cluster',
                path: () => `${bgJobs}/cluster`,
            },
        },

        effectiveAccess: {
            segment: 'effective-access',
            root: () => effAcc,
            dashboard: {
                segment: 'dashboard',
                path: () => `${effAcc}/dashboard`,
            },
            identities: {
                segment: 'identities',
                list: () => effIdentities,
                detail: (id: Id) => `${effIdentities}/${id}`,
            },
            systems: {
                segment: 'systems',
                list: () => effSystems,
                detail: (id: Id) => `${effSystems}/${id}`,
            },
            compare: {
                segment: 'compare',
                path: () => `${effAcc}/compare`,
            },
        },

        provisioning: {
            segment: 'access-provisioning',
            dashboard: {
                segment: 'dashboard',
                path: () => `${prov}/dashboard`,
            },
            tasks: {
                segment: 'tasks',
                path: () => `${prov}/tasks`,
                detail: (id: Id) => `${prov}/tasks/${id}`,
            },
            history: {
                segment: 'history',
                path: () => `${prov}/history`,
            },
        },

        auditLog: {
            segment: 'audit-log',
            path: () => auditLog,
        },

        reporting: {
            segment: 'reporting',
            root: () => reporting,
            categories: {
                segment: 'categories',
                path: () => `${reporting}/categories`,
            },
            reports: {
                segment: 'reports',
                detail: (id: Id) => `${reporting}/reports/${id}`,
                edit: (id: Id) => `${reporting}/reports/${id}/edit`,
                new: (categoryId: string) => `${reporting}/reports/new?categoryId=${categoryId}`,
            },
        },
    }
})()

/**
 * Resolve the detail-page URL for an entity type carried by an inline
 * entity-link token (issue 979 — see {@link renderEntityLinks} in the toaster).
 * {@code entityType} is the closed {@link EntityType} value used in the JSON
 * token ({@code IDENTITY}, {@code PARTY}, …). Returns {@code null} when no
 * detail page exists for the type — the toast then renders the display name
 * as plain text. The helper is intentionally generic: any future message
 * embedding an entity-link token reuses this resolver, no per-error-code
 * wiring needed.
 */
const ENTITY_DETAIL_ROUTES: Readonly<Partial<Record<EntityType, (id: Id) => string>>> = {
    [EntityType.IDENTITY]: (id) => routes.identityManagement.identities.detail(id),
    [EntityType.PARTY]: (id) => routes.identityManagement.parties.detail(id),
    [EntityType.RESOURCE]: (id) => routes.systems.resources.detail(id),
    [EntityType.FUNCTION_GROUP]: (id) => routes.accessGovernance.functionGroups.detail(id),
    [EntityType.SYSTEM]: (id) => routes.systems.detail(id),
    [EntityType.ENFORCEMENT_OBJECT]: (id) => routes.systems.enforcementObjects.detail(id),
    [EntityType.ACCESS_PROFILE]: (id) => routes.accessGovernance.profiles.detail(id),
    [EntityType.CONNECTOR]: (id) => routes.connectors.outbound.detail(id),
    [EntityType.REPORT_EXECUTION]: (id) => routes.reporting.reports.detail(id),
}

export function entityDetailRoute(entityType: EntityType | string, id: Id): string | null {
    const resolver = ENTITY_DETAIL_ROUTES[entityType as EntityType]
    return resolver ? resolver(id) : null
}

/**
 * Walk the `routes` tree and return every no-argument leaf that yields a
 * static path (`.path()`, `.list()`, `.root()`). Functions that require
 * parameters (`.detail(id)`, `.edit(id)`, etc.) are skipped — they need a
 * caller-supplied id and can't be enumerated blind.
 *
 * Use this to enumerate every static page once — e.g. for accessibility
 * scans — so adding a new section to `routes` is picked up automatically
 * instead of needing a hand-maintained list that silently goes stale.
 */
export function enumerateStaticPaths(): string[] {
    const paths: string[] = []
    // Treat each subtree at the `routes` level as a "section" and walk
    // recursively. A no-argument `() => string` leaf emits a path. Any
    // function that needs a parameter is skipped.
    const seen = new Set<string>()
    const visit = (node: unknown): void => {
        if (node == null || typeof node !== "object") return
        for (const value of Object.values(node)) {
            if (typeof value === "function" && value.length === 0) {
                try {
                    const result = (value as () => unknown)()
                    if (typeof result === "string" && result.startsWith("/") && !seen.has(result)) {
                        seen.add(result)
                        paths.push(result)
                    }
                } catch (_e) {
                    // A leaf that throws when called without args is not
                    // enumerable — skip it. Safe to swallow: the enumerator
                    // is best-effort, and any throwing leaf simply drops
                    // out of the static-path list.
                    void _e
                }
            } else if (typeof value === "object") {
                visit(value)
            }
        }
    }
    visit(routes)
    return paths
}
